@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.enginemodule

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import org.example.project.cleaning.SharedWorkspaceStyle
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.ScreenManager
import org.example.project.service.LizenzService
import org.example.project.service.ModuleImageUploadService
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.files.File

@Composable
fun CreateDetailLayout(
    module: All,
    onChange: (All) -> Unit,
    onClose: () -> Unit,
    onLoadModuleAssets: suspend () -> List<Int>,
    onUploadModuleAsset: suspend (File, String) -> Int?,
    onDeleteModuleAsset: suspend (Int) -> Boolean
) {
    val scope = rememberCoroutineScope()
    var localInfo by remember(module.modulId) { mutableStateOf<String?>(null) }
    var showAssetGallery by remember(module.modulId) { mutableStateOf(false) }
    var showScreenSelector by remember(module.modulId) { mutableStateOf(false) }
    var selectedImageIndex by remember(module.modulId) { mutableStateOf(0) }
    var moduleTags by remember { mutableStateOf<List<String>>(emptyList()) }
    var showTagInput by remember { mutableStateOf(false) }
    var newTag by remember { mutableStateOf("") }
    var tagBusy by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        moduleTags = ModuleApi.loadModuleTags()
    }

    val galleryAssetIds = module.moduleGalleryAssetIds.filter { it > 0 }.distinct()
    if (selectedImageIndex > galleryAssetIds.lastIndex) selectedImageIndex = galleryAssetIds.lastIndex.coerceAtLeast(0)
    val selectedAssetId = galleryAssetIds.getOrNull(selectedImageIndex)
    val selectedImagePath = selectedAssetId?.let(::moduleAssetUrl).orEmpty()
    val descriptionHeightPx = maxOf(220, (module.modulBeschreibung.lineSequence().count().coerceAtLeast(7) * 24) + 22)
    val moduleCategory = resolveModuleCategory(module)

    Div({ classes(SharedWorkspaceStyle.columnList) }) {
        Div({
            style {
                width(100.percent); maxWidth(1312.px); paddingBottom(40.px); boxSizing("border-box")
            }
        }) {
            Div({
                style {
                    width(100.percent); padding(20.px); borderRadius(24.px)
                    property("background", DashboardTheme.PREMIUM_SURFACE_BG)
                    property("border", "1px solid rgba(255,255,255,0.10)")
                    property("box-shadow", "0 20px 50px rgba(0,0,0,0.24)")
                    display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(14.px); boxSizing("border-box")
                }
            }) {
                Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(10.px) } }) {
                    Input(InputType.Text, attrs = {
                        value(module.modulName)
                        onInput { onChange(module.copy(modulName = it.value)) }
                        style {
                            width(100.percent); padding(8.px, 10.px); boxSizing("border-box"); textAlign("center")
                            fontSize(22.px); fontWeight("700"); color(Color("#f4f6f8"))
                            backgroundColor(Color("rgba(255,255,255,0.03)")); property("border", "1px solid rgba(255,255,255,0.10)"); borderRadius(10.px)
                        }
                    })
                    Img(src = "images/miniclose.svg", attrs = {
                        onClick { onClose() }
                        style { width(14.px); height(14.px); cursor("pointer"); property("filter", "brightness(0) invert(1)") }
                    })
                }

                Div({
                    style {
                        height(360.px); borderRadius(18.px); overflow("hidden"); position(Position.Relative)
                        property("border", "1px solid #2a2d34")
                        backgroundColor(Color("rgba(255,255,255,0.03)"))
                    }
                }) {
                    if (selectedImagePath.isNotBlank()) {
                        Img(src = selectedImagePath, attrs = { style { width(100.percent); height(100.percent); property("object-fit", "cover") } })
                    } else {
                        Span({
                            style { position(Position.Absolute); left(50.percent); top(50.percent); property("transform", "translate(-50%,-50%)"); color(Color("#93a0b3")); fontSize(14.px) }
                        }) { Text("Noch kein Coverbild") }
                    }
                    if (galleryAssetIds.size > 1) {
                        Div({ style { position(Position.Absolute); left(12.px); top(50.percent); property("transform", "translateY(-50%)"); width(40.px) } }) {
                            LumaticaButton(label = "<", onClick = { if (selectedImageIndex > 0) selectedImageIndex-- }, variant = LumaticaButtonVariant.Standard, enabled = selectedImageIndex > 0, fillWidth = true)
                        }
                        Div({ style { position(Position.Absolute); right(12.px); top(50.percent); property("transform", "translateY(-50%)"); width(40.px) } }) {
                            LumaticaButton(label = ">", onClick = { if (selectedImageIndex < galleryAssetIds.lastIndex) selectedImageIndex++ }, variant = LumaticaButtonVariant.Standard, enabled = selectedImageIndex < galleryAssetIds.lastIndex, fillWidth = true)
                        }
                    }
                    Div({ style { position(Position.Absolute); right(14.px); bottom(14.px); width(160.px) } }) {
                        LumaticaButton(label = "Bild hinzufügen", onClick = { showAssetGallery = true }, variant = LumaticaButtonVariant.Action, fillWidth = true)
                    }
                }

                if (galleryAssetIds.isNotEmpty()) {
                    Div({ style { display(DisplayStyle.Flex); gap(10.px); flexWrap(FlexWrap.Wrap); justifyContent(JustifyContent.Center) } }) {
                        galleryAssetIds.forEachIndexed { index, assetId ->
                            Div({
                                style {
                                    width(120.px); height(84.px); position(Position.Relative); overflow("hidden"); borderRadius(14.px); cursor("pointer"); boxSizing("border-box")
                                    property("border", if (index == selectedImageIndex) "2px solid #FF0000" else "1px solid rgba(255,255,255,0.08)")
                                }
                                onClick { selectedImageIndex = index }
                            }) {
                                Img(src = moduleAssetUrl(assetId), attrs = { style { width(100.percent); height(100.percent); property("object-fit", "cover") } })
                                Span({
                                    style {
                                        position(Position.Absolute); top(4.px); right(4.px); width(22.px); height(22.px); borderRadius(999.px)
                                        backgroundColor(Color("rgba(0,0,0,0.72)")); color(Color.white); textAlign("center"); lineHeight("22px"); cursor("pointer")
                                    }
                                    onClick {
                                        it.stopPropagation()
                                        val next = galleryAssetIds.filterNot { id -> id == assetId }
                                        onChange(module.copy(moduleGalleryAssetIds = next))
                                    }
                                }) { Text("×") }
                            }
                        }
                    }
                }

                TextArea(attrs = {
                    value(module.modulBeschreibung)
                    onInput { onChange(module.copy(modulBeschreibung = it.value)) }
                    style {
                        width(100.percent); height(descriptionHeightPx.px); padding(10.px); boxSizing("border-box"); property("resize", "vertical")
                        borderRadius(10.px); property("border", "1px solid rgba(255,255,255,0.10)")
                        backgroundColor(Color("rgba(255,255,255,0.03)")); color(Color("#aeb7c6")); fontSize(14.px); lineHeight("1.5")
                    }
                })

                Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center); gap(12.px); flexWrap(FlexWrap.Wrap) } }) {
                    Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px) } }) {
                        Span({ style { color(Color("#d2d8e3")); fontSize(13.px); fontWeight("600") } }) { Text("Version") }
                        Input(InputType.Text, attrs = {
                            value(module.modulReferenz); onInput { onChange(module.copy(modulReferenz = it.value)) }
                            style { width(130.px); padding(8.px); borderRadius(8.px); property("border", "1px solid rgba(255,255,255,0.10)"); backgroundColor(Color("rgba(255,255,255,0.03)")); color(Color("#d2d8e3")); boxSizing("border-box") }
                        })
                    }
                    Span({
                        style { color(Color("#d2d8e3")); fontSize(13.px); cursor("pointer"); textDecoration("underline") }
                        onClick { showScreenSelector = true }
                    }) { Text("Angebundene Screens: ${module.modulScreens.count { it.value }}") }
                    Div({ style { width(220.px); maxWidth(100.percent) } }) {
                        AppDropdown(
                            options = moduleTags.map { All(key = it, label = it) } + All(key = "__add_tag__", label = "+ Tag hinzufügen"),
                            selectedValue = moduleCategory,
                            onChange = {
                                if (it == "__add_tag__") showTagInput = true
                                else onChange(applyModuleCategory(module, it))
                            },
                            placeholder = "Tag auswählen",
                            appearance = AppDropdownAppearance.Field
                        )
                    }
                }
                localInfo?.let { P({ style { margin(0.px); color(Color("#8fd2ff")); fontSize(12.px) } }) { Text(it) } }
            }
        }

        if (showTagInput) {
            Div({
                style { position(Position.Fixed); property("inset", "0"); backgroundColor(rgba(0,0,0,0.55)); display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); property("z-index", "5200") }
                onClick { if (!tagBusy) { newTag = ""; showTagInput = false } }
            }) {
                Div({
                    style { width(380.px); maxWidth(90.percent); padding(18.px); borderRadius(14.px); backgroundColor(Color("#11151d")); property("border", "1px solid #2b3240"); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(14.px); boxSizing("border-box") }
                    onClick { it.stopPropagation() }
                }) {
                    H4({ style { margin(0.px); color(Color("#f4f6f8")); fontSize(18.px) } }) { Text("Tag hinzufügen") }
                    Input(InputType.Text, attrs = {
                        value(newTag)
                        onInput { newTag = it.value }
                        attr("placeholder", "Tag eingeben")
                        style { width(100.percent); padding(10.px); borderRadius(8.px); property("border", "1px solid rgba(255,255,255,0.10)"); backgroundColor(Color("rgba(255,255,255,0.03)")); color(Color("#d2d8e3")); boxSizing("border-box") }
                    })
                    Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.FlexEnd); gap(8.px) } }) {
                        LumaticaButton(label = "Abbrechen", onClick = { newTag = ""; showTagInput = false }, variant = LumaticaButtonVariant.Standard, enabled = !tagBusy)
                        LumaticaButton(
                            label = "Speichern",
                            onClick = {
                                val tag = normalizeModuleTag(newTag)
                                if (tag.isNotBlank()) {
                                    scope.launch {
                                        tagBusy = true
                                        moduleTags = ModuleApi.saveModuleTag(tag)
                                        onChange(applyModuleCategory(module, tag))
                                        newTag = ""
                                        showTagInput = false
                                        tagBusy = false
                                    }
                                }
                            },
                            variant = LumaticaButtonVariant.Action,
                            enabled = !tagBusy && normalizeModuleTag(newTag).isNotBlank()
                        )
                    }
                }
            }
        }

        if (showScreenSelector) {
            Div({
                style { position(Position.Fixed); property("inset", "0"); backgroundColor(rgba(0,0,0,0.45)); display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); property("z-index", "5100") }
                onClick { showScreenSelector = false }
            }) {
                Div({
                    style { width(520.px); maxHeight(70.vh); overflowY("auto"); backgroundColor(Color("#11151d")); borderRadius(12.px); property("border", "1px solid #2b3240"); padding(14.px); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(10.px) }
                    onClick { it.stopPropagation() }
                }) {
                    H4({ style { margin(0.px); color(Color("#f4f6f8")); fontSize(18.px) } }) { Text("Screens anbinden") }
                    Div({ style { display(DisplayStyle.Flex); flexWrap(FlexWrap.Wrap); gap(8.px) } }) {
                        LizenzService.getShopConfiguredScreens().sortedBy { ScreenManager.administrativeLabelForKey(it).lowercase() }.forEach { key ->
                            val enabled = module.modulScreens[key] == true
                            LumaticaButton(
                                label = ScreenManager.administrativeLabelForKey(key),
                                onClick = { onChange(module.copy(modulScreens = module.modulScreens + (key to !enabled))) },
                                variant = LumaticaButtonVariant.State,
                                isActive = enabled,
                                wrapLabel = true
                            )
                        }
                    }
                    Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.FlexEnd) } }) { LumaticaButton(label = "Fertig", onClick = { showScreenSelector = false }, variant = LumaticaButtonVariant.Standard) }
                }
            }
        }

        ModuleImageUploadService(
            visible = showAssetGallery,
            targetAssetKey = "cover-gallery",
            onClose = { showAssetGallery = false },
            onLoadModuleAssets = onLoadModuleAssets,
            onUploadModuleAsset = onUploadModuleAsset,
            onDeleteModuleAsset = onDeleteModuleAsset,
            onAssetSelected = { assetId, _ ->
                val next = (module.moduleGalleryAssetIds + assetId).filter { it > 0 }.distinct()
                selectedImageIndex = next.indexOf(assetId).coerceAtLeast(0)
                onChange(module.copy(moduleGalleryAssetIds = next))
            },
            onAssetDeleted = { assetId, _ -> onChange(module.copy(moduleGalleryAssetIds = module.moduleGalleryAssetIds.filterNot { it == assetId })) },
            onInfo = { localInfo = it }
        )
    }
}
